/**
 * Худалдан авагчийн баримт — вэбийн кассаас браузераар хэвлэнэ.
 *
 * Агуулга нь POS-ийн Sunmi цаастай ИЖИЛ (`receiptRows`, @boots/domain),
 * серверт хөлдсөн захиалгаас (`GET /orders/:id`) — маягтын утгаас биш.
 *
 * ⚠️ Браузер хэвлэх цонхоо ҮРГЭЛЖ харуулна (принтер сонгох). Цонхгүй
 *    хэвлэх нь зөвхөн Chrome-ийн `--kiosk-printing` горимд.
 * ⚠️ Хэвлэлт унасан, цуцлагдсан ч борлуулалт ХҮЧИНТЭЙ — захиалга аль
 *    хэдийн серверт. Товч нь үлдэнэ, дахин хэвлэж болно.
 * ⚠️ Цаас нь `#root`-ийн ГАДНА (portal) — хэвлэхэд апп бүхэлдээ нуугдаж
 *    зөвхөн баримт гарна (`index.css` → `.print-receipt`).
 */

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { receiptFromOrder, receiptRows, type ReceiptOrder } from '@boots/domain';
import { api, type Envelope } from '../lib/api';

export function ReceiptPrint({ orderId, autoPrint }: { orderId: string; autoPrint: boolean }) {
  const q = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => api<Envelope<ReceiptOrder>>(`/orders/${orderId}`),
  });
  const order = q.data?.data;
  // StrictMode-д effect 2 удаа ажилладаг — цонх НЭГ л удаа нээгдэнэ
  const printed = useRef(false);

  useEffect(() => {
    if (!autoPrint || !order || printed.current) return;
    printed.current = true;
    // Цаас DOM-д буусны дараагийн фрэймд — эс тэгвээс хоосон хуудас гарна
    requestAnimationFrame(() => window.print());
  }, [autoPrint, order]);

  return (
    <>
      <button
        type="button"
        disabled={!order}
        onClick={() => window.print()}
        className="inline-flex min-h-11 items-center justify-center rounded-btn border border-hair bg-input px-4 py-2 text-sm font-medium text-ink transition hover:bg-accent-100 active:scale-[0.97] disabled:opacity-50 sm:min-h-0"
      >
        {q.isError ? 'Баримт татагдсангүй' : 'Баримт хэвлэх'}
      </button>
      {order
        ? createPortal(
            <div className="print-receipt">
              {receiptRows(receiptFromOrder(order)).map((r, i) => {
                switch (r.kind) {
                  case 'text':
                    return <p key={i} className="text-center">{r.text}</p>;
                  case 'pair':
                    return (
                      <p key={i} className={`flex justify-between gap-3 ${r.strong ? 'font-semibold' : ''}`}>
                        <span>{r.label}</span>
                        <span className="num whitespace-nowrap">{r.value}</span>
                      </p>
                    );
                  case 'rule':
                    return <hr key={i} />;
                  case 'gap':
                    return <div key={i} className="h-2" />;
                }
              })}
              <p className="mt-2 text-center">Баярлалаа!</p>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
